"use client";
// ────────── UI components — from Claude Design prototype ──────────
import React from 'react';
import { VColor, VFont, verdictTone, confidenceInfo, type ConfidenceLevel } from './tokens';
import { IconStar } from './icons';

// ────────── Confidence pill ──────────
export const ConfidenceBadge: React.FC<{
  level?: ConfidenceLevel;
  onClick?: () => void;
}> = ({ level = 'haute', onClick }) => {
  const m = confidenceInfo(level);
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 7, height: 26, padding: '0 12px',
      background: 'transparent', color: VColor.ink2, border: `1px solid ${VColor.line}`,
      borderRadius: 999, cursor: 'pointer',
      fontFamily: VFont.sans, fontSize: 11, fontWeight: 500, letterSpacing: 0.1,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: m.tone, display: 'inline-block' }} />
      <span>{m.label}</span>
    </button>
  );
};

// ────────── Flag chip (anti-piège) ──────────
export const FlagBadge: React.FC<{
  kind?: 'destructive' | 'info';
  children: React.ReactNode;
}> = ({ kind = 'info', children }) => {
  const styles = kind === 'destructive'
    ? { bg: VColor.badSoft, fg: VColor.bad }
    : { bg: VColor.cream, fg: VColor.ink2 };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, height: 24, padding: '0 9px',
      background: styles.bg, color: styles.fg, borderRadius: 6,
      fontSize: 12, fontWeight: 500, fontFamily: VFont.sans, lineHeight: 1,
    }}>
      {kind === 'destructive' && <span style={{ fontSize: 10 }}>{'⚠'}</span>}
      {children}
    </span>
  );
};

// ────────── Verdict pill (refined: mono caps + numeric ref, no emoji) ──────────
export const VerdictPill: React.FC<{
  text: string;
  sub?: string;
  big?: boolean;
}> = ({ text, sub, big = false }) => {
  const t = verdictTone(text);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      fontFamily: VFont.mono, color: t.fg,
    }}>
      <span style={{
        width: big ? 7 : 6, height: big ? 7 : 6, borderRadius: 999,
        background: t.mark, display: 'inline-block',
      }} />
      <span style={{
        fontSize: big ? 11 : 10, fontWeight: 600,
        letterSpacing: big ? 1.8 : 1.4, textTransform: 'uppercase' as const,
      }}>{text}</span>
      {sub && (
        <span style={{
          fontSize: big ? 11 : 10, color: VColor.muted, fontWeight: 500,
          letterSpacing: 0.6,
        }}>{'·'} {sub}</span>
      )}
    </span>
  );
};

// ────────── Budget bar ──────────
export const BudgetBar: React.FC<{
  pct?: number;
  height?: number;
}> = ({ pct = 0, height = 12 }) => {
  const tone = pct < 50 ? VColor.good : pct < 80 ? VColor.warn : VColor.bad;
  const w = Math.min(100, Math.max(0, pct));
  return (
    <div role="progressbar" aria-valuenow={w} aria-valuemin={0} aria-valuemax={100}
      aria-label={`Budget utilisé : ${w}%`}
      style={{
        height, width: '100%', background: VColor.line2, borderRadius: 999, overflow: 'hidden',
        position: 'relative',
      }}>
      <div style={{
        height: '100%', width: `${w}%`, background: tone, borderRadius: 999,
        transition: 'width 800ms cubic-bezier(.2,.8,.2,1)',
      }} />
    </div>
  );
};

// ────────── Sparkline ──────────
export const Sparkline: React.FC<{
  values: number[];
  w?: number;
  h?: number;
  threshold?: number;
}> = ({ values, w = 110, h = 36, threshold }) => {
  // Audit 23 P1 #11 : guard division par zéro si 0 ou 1 valeur
  if (!values?.length || values.length < 2) return null;
  const min = Math.min(...values, threshold ?? Infinity);
  const max = Math.max(...values, threshold ?? -Infinity);
  const range = Math.max(1, max - min);
  const stepX = w / (values.length - 1);
  const pts = values.map((v, i) => [i * stepX, h - ((v - min) / range) * (h - 4) - 2]);
  const d = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(' ');
  const area = `${d} L${w},${h} L0,${h} Z`;
  const last = pts[pts.length - 1];
  const safeIdx = Math.max(0, values.length - 8);
  const trend = values[values.length - 1] - values[safeIdx];
  const stroke = trend < 0 ? VColor.good : trend > 0 ? VColor.bad : VColor.muted;
  const fill = trend < 0 ? VColor.goodSoft : trend > 0 ? VColor.badSoft : VColor.line2;

  const thresholdY = threshold != null
    ? h - ((threshold - min) / range) * (h - 4) - 2
    : null;

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <path d={area} fill={fill} opacity="0.6" />
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {thresholdY != null && (
        <line x1="0" x2={w} y1={thresholdY} y2={thresholdY} stroke={VColor.ink} strokeDasharray="2 3" strokeWidth="1" opacity="0.35" />
      )}
      <circle cx={last[0]} cy={last[1]} r="2.5" fill={stroke} />
    </svg>
  );
};

// ────────── Star/rating cluster ──────────
export const Rating: React.FC<{
  value: number;
  reviews?: number | null;
  size?: number;
}> = ({ value, reviews, size = 12 }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: 12, fontFamily: VFont.sans, color: VColor.ink,
  }}>
    <IconStar size={size} style={{ color: '#1A2C24' }} />
    <strong style={{ fontWeight: 600 }}>{value.toFixed(1)}</strong>
    {reviews != null && <span style={{ color: VColor.muted, fontWeight: 400 }}>{'·'} {reviews} avis</span>}
  </span>
);

// ────────── Card shell ──────────
export const Card: React.FC<{
  children: React.ReactNode;
  hero?: boolean;
  style?: React.CSSProperties;
}> = ({ children, hero = false, style = {} }) => (
  <div style={{
    background: VColor.paper,
    borderRadius: 18,
    border: hero ? `1.5px solid ${VColor.ink}` : `1px solid ${VColor.line}`,
    boxShadow: hero
      ? '0 24px 48px -16px rgba(15,15,15,0.18), 0 2px 6px rgba(15,15,15,0.04)'
      : '0 1px 2px rgba(15,15,15,0.04)',
    overflow: 'hidden',
    ...style,
  }}>{children}</div>
);

// ────────── Section header ──────────
export const Eyebrow: React.FC<{
  children: React.ReactNode;
  color?: string;
}> = ({ children, color = VColor.muted }) => (
  <div style={{
    fontFamily: VFont.mono, fontSize: 10, fontWeight: 500, color,
    letterSpacing: 1.2, textTransform: 'uppercase' as const,
  }}>{children}</div>
);

export const SerifHead: React.FC<{
  children: React.ReactNode;
  size?: number;
  style?: React.CSSProperties;
}> = ({ children, size = 28, style = {} }) => (
  <h1 style={{
    fontFamily: VFont.serif, fontWeight: 400, fontSize: size,
    lineHeight: 1.05, letterSpacing: -0.3, color: VColor.ink, margin: 0,
    ...style,
  }}>{children}</h1>
);

// ────────── V3.0 — Prix type badge ──────────
export const PrixTypeBadge: React.FC<{
  type?: string;
}> = ({ type = 'estimation' }) => {
  const config: Record<string, { label: string; bg: string; fg: string }> = {
    live: { label: 'Prix live', bg: VColor.goodSoft, fg: VColor.good },
    fourchette: { label: 'Fourchette', bg: VColor.warnSoft, fg: VColor.warn },
    estimation: { label: 'Estimation', bg: VColor.cream, fg: VColor.muted },
  };
  const c = config[type] || config.estimation;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 7px',
      background: c.bg, color: c.fg, borderRadius: 4,
      fontSize: 10, fontWeight: 600, fontFamily: VFont.mono,
      letterSpacing: 0.4, textTransform: 'uppercase' as const,
    }}>{c.label}</span>
  );
};

// ────────── V3.0 — Friction badge ──────────
export const FrictionBadge: React.FC<{
  label?: string;
  severity?: string;
}> = ({ label = 'Fluide', severity = 'good' }) => {
  const toneMap: Record<string, { bg: string; fg: string }> = {
    good: { bg: VColor.goodSoft, fg: VColor.good },
    muted: { bg: VColor.cream, fg: VColor.muted },
    warn: { bg: VColor.warnSoft, fg: VColor.warn },
    bad: { bg: VColor.badSoft, fg: VColor.bad },
  };
  const tone = toneMap[severity] || toneMap.muted;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 8px',
      background: tone.bg, color: tone.fg, borderRadius: 6,
      fontSize: 11, fontWeight: 600, fontFamily: VFont.sans,
    }}>{label}</span>
  );
};

// ────────── Button ──────────
export const VButton: React.FC<{
  children: React.ReactNode;
  variant?: 'primary' | 'dark' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  full?: boolean;
  leadingIcon?: React.ReactNode;
  style?: React.CSSProperties;
  type?: 'button' | 'submit';
}> = ({ children, variant = 'primary', size = 'md', onClick, full, leadingIcon, style = {}, type = 'button' }) => {
  const h = size === 'sm' ? 36 : size === 'lg' ? 52 : 44;
  const radius = 12;
  const bg: Record<string, string> = {
    primary: VColor.accent,
    dark: VColor.ink,
    ghost: 'transparent',
    outline: 'transparent',
  };
  const fg: Record<string, string> = {
    primary: '#fff',
    dark: '#fff',
    ghost: VColor.ink,
    outline: VColor.ink,
  };
  const border: Record<string, string> = {
    primary: 'none',
    dark: 'none',
    ghost: 'none',
    outline: `1.25px solid ${VColor.ink}`,
  };
  return (
    <button type={type} onClick={onClick} style={{
      height: h, padding: `0 ${size === 'sm' ? 14 : 18}px`, borderRadius: radius,
      background: bg[variant], color: fg[variant], border: border[variant], cursor: 'pointer',
      fontFamily: VFont.sans, fontSize: size === 'sm' ? 13 : 14, fontWeight: 600,
      width: full ? '100%' : undefined,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      transition: 'transform 120ms ease, opacity 120ms ease',
      ...style,
    }}>
      {leadingIcon}
      {children}
    </button>
  );
};
